import { useEffect, useRef, useState } from "react";
import { ClipboardList, Loader2, Plus } from "lucide-react";
import { ehPapelDeDentista, type StatusDoPlano } from "@bookdental/schemas";
import { Button } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/lib/auth";
import { usePermissao } from "@/lib/use-permissao";
import { EditorDoPlano } from "./EditorDoPlano";
import { SeloDoStatus } from "./SeloDoStatus";
import { reais } from "./use-plano";

/** Aba Tratamentos da ficha: os planos do paciente (cada um é também o orçamento). */
export function AbaTratamentos({
  patientId,
  nascimento,
  sexo,
  pedidoDeNovo = 0,
}: {
  patientId: string;
  nascimento?: Date | string | null;
  /** Sexo do paciente — escolhe o modelo do desenho da face. */
  sexo?: string | null;
  /** Muda quando a ficha pede "Criar plano". */
  pedidoDeNovo?: number;
}) {
  const { user } = useAuth();
  const lista = trpc.plano.listarDoPaciente.useQuery({ patientId });
  const podeMontar = usePermissao("treatments.manage");
  const [aberto, setAberto] = useState<string | "novo" | null>(null);
  // A maioria dos pacientes tem um plano em curso: a aba abre direto nele (uma vez só — voltar
  // pra lista continua valendo).
  const abriuSozinho = useRef(false);
  useEffect(() => {
    if (pedidoDeNovo > 0) setAberto("novo");
  }, [pedidoDeNovo]);
  useEffect(() => {
    if (abriuSozinho.current || !lista.data || pedidoDeNovo > 0) return;
    abriuSozinho.current = true;
    const ativos = lista.data.filter((p) => p.status === "APROVADO" || p.status === "RASCUNHO" || p.status === "APRESENTADO");
    if (ativos.length === 1) setAberto(ativos[0].id);
  }, [lista.data, pedidoDeNovo]);

  if (aberto) {
    return (
      <EditorDoPlano
        key={aberto}
        planoId={aberto === "novo" ? null : aberto}
        patientId={patientId}
        nascimento={nascimento}
        sexo={sexo}
        // Quem monta o plano costuma ser o próprio dentista — já entra como responsável.
        dentistaInicial={user && ehPapelDeDentista(user.activeOrganization?.role) ? user.id : null}
        onVoltar={() => setAberto(null)}
      />
    );
  }

  const planos = lista.data ?? [];
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-zinc-900 dark:text-foreground">Planos de tratamento</h2>
          <p className="text-[12.5px] text-zinc-500 dark:text-muted-foreground">Cada plano é também o orçamento que o paciente leva.</p>
        </div>
        {podeMontar && (
          <Button size="sm" onClick={() => setAberto("novo")}>
            <Plus className="mr-1 h-3.5 w-3.5" />
            Novo plano
          </Button>
        )}
      </div>
      {lista.isLoading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
        </div>
      ) : planos.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-zinc-200 py-12 text-center">
          <ClipboardList className="h-6 w-6 text-zinc-300" />
          <p className="text-sm text-zinc-500">Nenhum plano de tratamento ainda.</p>
        </div>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2">
          {planos.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => setAberto(p.id)}
                className="w-full rounded-xl border border-zinc-200 bg-white p-4 text-left shadow-sm transition-colors hover:border-brand-300 dark:border-border dark:bg-card"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-zinc-900 dark:text-foreground">
                    Plano nº {p.numero}
                    {p.titulo ? ` · ${p.titulo}` : ""}
                  </span>
                  <SeloDoStatus status={p.status as StatusDoPlano} />
                </div>
                <div className="mt-2 flex items-center justify-between text-[12.5px] text-zinc-500 dark:text-muted-foreground">
                  <span>
                    {p.itens} procedimento{p.itens === 1 ? "" : "s"}
                    {p.status === "APROVADO" || p.status === "CONCLUIDO" ? ` · ${p.feitos} feito${p.feitos === 1 ? "" : "s"}` : ""}
                  </span>
                  <span className="font-semibold text-zinc-900 dark:text-foreground">{reais(p.total)}</span>
                </div>
                <p className="mt-1 text-[11px] text-zinc-400">
                  {new Date(p.criadoEm).toLocaleDateString("pt-BR")}
                  {p.entregueEm ? ` · orçamento entregue em ${new Date(p.entregueEm).toLocaleDateString("pt-BR")}` : ""}
                </p>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
